import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatMessage, ChatSession } from '../types';
import { generateId } from '../utils';

interface ChatState {
  sessions: ChatSession[];
  currentSessionId: string | null;
  isLoading: boolean;
  error: string | null;
  streamingContent: string;
  isStreaming: boolean;
  
  // AI联动：存储最后一条AI回复内容
  lastAIResponse: string | null;
  lastAIResponseTimestamp: number | null;

  // Actions
  createSession: (title?: string) => string;
  setCurrentSession: (sessionId: string) => void;
  addMessage: (sessionId: string, message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  updateMessage: (sessionId: string, messageId: string, updates: Partial<ChatMessage>) => void;
  deleteMessage: (sessionId: string, messageId: string) => void;
  clearMessages: (sessionId: string) => void;
  deleteSession: (sessionId: string) => void;
  clearSessions: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setStreamingContent: (content: string) => void;
  appendStreamingContent: (content: string) => void;
  setIsStreaming: (isStreaming: boolean) => void;
  clearStreamingContent: () => void;
  getCurrentSession: () => ChatSession | undefined;
  getSessionMessages: (sessionId: string) => ChatMessage[];
  updateSessionTitle: (sessionId: string, title: string) => void;
  
  // AI联动Actions
  setLastAIResponse: (response: string) => void;
  clearLastAIResponse: () => void;
  getLastAIResponse: () => string | null;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      sessions: [],
      currentSessionId: null,
      isLoading: false,
      error: null,
      streamingContent: '',
      isStreaming: false,
      lastAIResponse: null,
      lastAIResponseTimestamp: null,

      createSession: (title = '新对话') => {
        const newSession: ChatSession = {
          id: generateId(),
          title,
          messages: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
        set((state) => ({
          sessions: [newSession, ...state.sessions],
          currentSessionId: newSession.id,
        }));
        return newSession.id;
      },

      setCurrentSession: (sessionId: string) => {
        set({ currentSessionId: sessionId });
      },

      addMessage: (sessionId: string, message) => {
        const newMessage: ChatMessage = {
          ...message,
          id: generateId(),
          timestamp: Date.now(),
        };
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  messages: [...session.messages, newMessage],
                  updatedAt: Date.now(),
                  title:
                    session.messages.length === 0 && message.role === 'user'
                      ? message.content.slice(0, 20) + (message.content.length > 20 ? '...' : '')
                      : session.title,
                }
              : session
          ),
        }));
        return newMessage.id;
      },

      updateMessage: (sessionId: string, messageId: string, updates) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  messages: session.messages.map((msg) =>
                    msg.id === messageId ? { ...msg, ...updates } : msg
                  ),
                  updatedAt: Date.now(),
                }
              : session
          ),
        }));
      },

      deleteMessage: (sessionId: string, messageId: string) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  messages: session.messages.filter((msg) => msg.id !== messageId),
                  updatedAt: Date.now(),
                }
              : session
          ),
        }));
      },

      clearMessages: (sessionId: string) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  messages: [],
                  updatedAt: Date.now(),
                }
              : session
          ),
        }));
      },

      deleteSession: (sessionId: string) => {
        set((state) => ({
          sessions: state.sessions.filter((s) => s.id !== sessionId),
          currentSessionId:
            state.currentSessionId === sessionId
              ? state.sessions.find((s) => s.id !== sessionId)?.id || null
              : state.currentSessionId,
        }));
      },

      clearSessions: () => {
        set({ sessions: [], currentSessionId: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      setStreamingContent: (content: string) => {
        set({ streamingContent: content });
      },

      appendStreamingContent: (content: string) => {
        set((state) => ({
          streamingContent: state.streamingContent + content,
        }));
      },

      setIsStreaming: (isStreaming: boolean) => {
        set({ isStreaming });
      },

      clearStreamingContent: () => {
        set({ streamingContent: '', isStreaming: false });
      },

      getCurrentSession: () => {
        const { sessions, currentSessionId } = get();
        return sessions.find((s) => s.id === currentSessionId);
      },

      getSessionMessages: (sessionId: string) => {
        const session = get().sessions.find((s) => s.id === sessionId);
        return session?.messages || [];
      },

      updateSessionTitle: (sessionId: string, title: string) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId ? { ...session, title } : session
          ),
        }));
      },

      // AI联动：设置最后AI回复
      setLastAIResponse: (response: string) => {
        set({
          lastAIResponse: response,
          lastAIResponseTimestamp: Date.now(),
        });
      },

      // AI联动：清除最后AI回复
      clearLastAIResponse: () => {
        set({
          lastAIResponse: null,
          lastAIResponseTimestamp: null,
        });
      },

      // AI联动：获取最后AI回复（已移除时间限制，只要存在就返回）
      getLastAIResponse: () => {
        return get().lastAIResponse;
      },
    }),
    {
      name: 'chat-storage',
      partialize: (state) => ({
        sessions: state.sessions,
        currentSessionId: state.currentSessionId,
        lastAIResponse: state.lastAIResponse,
        lastAIResponseTimestamp: state.lastAIResponseTimestamp,
      }),
    }
  )
);
